/**
 * Modal — a confirmação. Fonte: Camera.dc.html (`modalFechar`).
 *
 * Véu de bg a 70% com blur de 10, card centrado de raio 26 com borda de 8%,
 * título, corpo e as ações empilhadas: a principal em 48 de altura, pílula
 * clara com texto escuro; a secundária em 44, sem fundo, em text.3.
 *
 * É `role="dialog"` de verdade: o foco entra no card ao abrir, Esc fecha e o
 * toque no véu fecha — com a mesma saída do botão secundário, porque
 * desistir nunca deve ser a ação destrutiva.
 *
 * Não decide nada: quem chama diz o que cada botão faz. Um modal que
 * confirma exclusão passa `perigo` para a ação principal virar danger.
 *
 * ─── ELE SAI PELO PORTAL, E NÃO É DETALHE DE ESTILO ─────────────────────────
 *
 * Pendurado no `body`, e não onde foi escrito. Um diálogo renderizado no lugar
 * herda o contexto de quem o contém: o `.area` do Caminho isola z-index de
 * propósito (os nós chegam a 20), as fases usam `content-visibility`, e o card
 * acabava centrado na TRILHA — a uns três mil pixels do que a pessoa está
 * vendo. Nenhum ajuste de `position` conserta isso de dentro, porque o problema
 * é o ancestral, não a regra. Fora da árvore, não há ancestral.
 *
 * O `.raiz` é `fixed`; as duas coisas juntas é que dão "centro da tela".
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import haptics from '../../feedback/haptics';
import cx from '../../cx';
import s from './Modal.module.scss';

export default function Modal({
  aberto = true,
  titulo,
  corpo,
  acaoRotulo,
  onAcao,
  cancelarRotulo,
  onCancelar,
  carregando = false,
  perigo = false,
  children,
  className = '',
  ...rest
}) {
  const { t } = useTranslation(['comum']);
  const card = useRef(null);

  /* Foco entra no card e Esc fecha: é diálogo, não uma camada decorativa. */
  useEffect(() => {
    if (!aberto) return undefined;
    card.current?.focus();
    const aoTeclar = (e) => {
      if (e.key === 'Escape' && !carregando) onCancelar?.();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aberto, carregando, onCancelar]);

  if (!aberto) return null;

  const confirmar = () => {
    haptics.medium();
    onAcao?.();
  };

  const dialogo = (
    <div className={cx(s.raiz, className)} {...rest}>
      <button
        type="button"
        className={s.veu}
        aria-label={t('comum:acao.fechar')}
        onClick={() => !carregando && onCancelar?.()}
      />

      <div
        className={s.card}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        tabIndex={-1}
        ref={card}
      >
        <div className={s.textos}>
          {titulo && <h2 className={s.titulo}>{titulo}</h2>}
          {corpo && <p className={s.corpo}>{corpo}</p>}
        </div>

        {children}

        <div className={s.acoes}>
          {acaoRotulo && (
            <button
              type="button"
              className={cx(s.principal, perigo && s.perigo)}
              onClick={confirmar}
              disabled={carregando}
            >
              {carregando ? <span className={s.girando} aria-hidden="true" /> : acaoRotulo}
            </button>
          )}
          {onCancelar && (
            <button
              type="button"
              className={s.secundaria}
              onClick={onCancelar}
              disabled={carregando}
            >
              {cancelarRotulo || t('comum:acao.cancelar')}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  /* `document.body` só existe no navegador. No teste de render em ambiente sem
     DOM o portal não tem para onde ir, e devolver a árvore direto mantém o
     componente testável sem um mock de portal em cada suíte. */
  return typeof document === 'undefined'
    ? dialogo
    : createPortal(dialogo, document.body);
}
